/**
 * @overview
 * Fire `analytics.track` events when forms with the `data-form-tracker` attribute are submitted.
 *
 * ## Data Attributes
 * - `data-form-tracker`: Required in order for form tracker to find and track the form. Optionally takes the name of
 *                        the event that should be fired. This name is treated as the default if a
 *                        `data-form-tracker-event-name` attribute is not specified.
 * - `data-form-tracker-event-name`: (Optional) The name of the event that should be sent when the form is submitted.
 * - `data-form-tracker-arg-*`: (Optional) Additional data that should be sent with each event.
 *
 * ## Single Event Tracking
 * The simplest use case is when you want to track a single event for the form submission. To do this attach the
 * `data-form-tracker` attributes as well as any `data-form-tracker-arg-*` attributes to the form element. For example:
 *
 * ```
 * <form method="post" data-form-tracker="Mailchimp" data-form-tracker-arg-form-type="survey">
 * ...
 * ```
 *
 * This tells the form tracker that when submitted this form should trigger an event called `Mailchimp`
 * and should pass it a parameter called `form_type` that has a value of `survey`. The analytics call for
 * this would look like:
 *
 * ```
 * analytics.track("Mailchimp", { 'form_type': 'survey' });
 * ```
 *
 * ## Multi Event Tracking
 * The form tracker also allows you to register multiple events to track when a form is submitted. You can do this
 * by including a namespace on the `-arg-*` and `-event-name` data attributes. A namespace is some string that gets
 * used to link the data attributes to each other so that they get submitted for the same event.
 *
 * An example:
 * ```
 *  <form method="post"
 *      data-form-tracker
 *      data-form-tracker-mc-event-name="Mailchimp"
 *      data-form-tracker-mc-arg-form-type="mailchimp_survey"
 *      data-form-tracker-ss-event-name="SecondStreet"
 *      data-form-tracker-ss-arg-form-type="secondstreet_survey"
 *  >
 * ```
 *
 * In this example we are tracking the same event as before and a new event called `SecondStreet` by using
 * a namespace of `mc` and `ss` we can build out the events and match their data to the correct one.
 *
 * The track calls for this would look like:
 *
 * ```
 * analytics.track('Mailchimp', { 'form_type': 'survey' });
 * analytics.track('SecondStreet', { 'form_type': 'survey' });
 * ```
 */

import mutationObserver from 'utils/mutation_observer';
import {analytics} from "./analytics";
import $ from 'jquery';

const SELECTOR = '[data-form-tracker]';
const CONFIG_SELECTOR = '[data-form-tracker-config]';

/**
 * Handle submitting the form.
 *
 * Parses and sends the `analytics.track` commands. The form submission is blocked until either the analytics data has
 * successfully sent or a 500ms timeout has elapsed.
 *
 * @param event
 */
function handleSubmit(event) {
    const {target: form} = event;

    const formDataArray = $(form).serializeArray();
    const formData = new Map(formDataArray.map(entry => [entry.name, entry.value]));

    event.preventDefault();

    let formSubmitted = false;
    const submitForm = () => {
        if (!formSubmitted) {
            formSubmitted = true;
            form.submit();
        }
    };

    const configElements = form.querySelectorAll(CONFIG_SELECTOR);

    // No config found for sending events, so just submit the form.
    if (configElements.length === 0) {
        submitForm();
        return;
    }

    let eventsToTrack = {};
    for (let configEle of configElements) {
        const configJson = JSON.parse(configEle.textContent);
        for (let event of configJson.events) {
            let eventData = {};
            for (let data of event.data) {
                const key = data.key;
                let value = data.value;
                if (Object.prototype.hasOwnProperty.call(data, 'value_from_field')) {
                    const fieldData = formData.get(data.value_from_field);
                    if (typeof fieldData === 'undefined') {
                        continue;
                    }
                    value = fieldData;
                }
                eventData[key] = value;
            }
            eventsToTrack[event.name] = eventData;
        }
    }

    for (let [eventName, eventData] of Object.entries(eventsToTrack)) {
        analytics.track(eventName, eventData);
    }

    // Fallback in case the analytics.track callback doesn't fire or takes too long.
    setTimeout(submitForm, 500);
}

/**
 * Create our listeners for all elements that have the form tracker data attribute.
 */
window.addEventListener('DOMContentLoaded', () => {
    for (let el of document.querySelectorAll(SELECTOR))
        el.addEventListener('submit', handleSubmit);

    mutationObserver(SELECTOR, (index, element) => {
        element.addEventListener('submit', handleSubmit);
    })
});


// eslint-disable-next-line no-unused-vars
function fetchFieldValues(fieldSelector) {
    let fieldData = [];
    $(fieldSelector).each((index, field) => {
        if (field.is('input') || field.is('textarea') || field.is('select')) {
            const inputId = field.prop('id');
            let dataName = field.prop('name');
            let dataValue = field.val();
            let dataLabel = "";

            // Grab the label text if we have an id
            if (inputId) {
                const label = $(`label[for=${inputId}]`);
                if (label.length > 0) {
                    dataLabel = label.text().trim();
                }
            }

            if (field.is(':checkbox')) {
                dataValue = field.is(':checked');
            } else if (field.is(':radio')) {
                const selectedRadio = field.is(':checked');
                // Only track the radio buttons that are selected.
                if (selectedRadio.length === 0) {
                    return true;
                }
                dataValue = selectedRadio.text();
            } else if (field.is('select')) {
                dataValue = field.children('option:selected').text().trim();
            }

            fieldData.push({
                name: dataName,
                label: dataLabel,
                value: dataValue,
            });
        }
    });
    return fieldData;
}
